import { jwtDecode } from "jwt-decode";

const AUTH_TOKEN_STORAGE_KEY = "authToken";

const KEYCLOAK_SYSTEM_ROLES = new Set(["offline_access", "uma_authorization"]);

export const isKeycloakSystemRole = (role) => {
  const normalized = String(role || "").toLowerCase();
  return (
    KEYCLOAK_SYSTEM_ROLES.has(normalized) ||
    normalized.startsWith("default-roles-")
  );
};

/**
 * Decodes a JWT access token into a normalized user profile.
 * Returns null when the token is missing, invalid, or expired.
 */
export const getUserInfoFromToken = (token) => {
  if (!token || typeof token !== "string") return null;

  try {
    const decoded = jwtDecode(token);

    if (!decoded || typeof decoded !== "object") return null;

    const realmRoles = Array.isArray(decoded.realm_access?.roles)
      ? decoded.realm_access.roles
      : [];
    const clientRoles = Array.isArray(decoded?.resource_access?.plambo?.roles)
      ? decoded.resource_access.plambo.roles
      : [];
    const roles = [...new Set([...clientRoles, ...realmRoles])].filter(
      (role) => !isKeycloakSystemRole(role),
    );

    return {
      id: decoded.sub || "",
      name: decoded.name || "",
      given_name: decoded.given_name || "",
      username: decoded.preferred_username || "",
      email: decoded.email || "",
      roles,
    };
  } catch {
    return null;
  }
};

/**
 * Reads the stored auth token from localStorage and returns the decoded user profile.
 */
export const getAuthenticatedUserFromStorage = () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    return token ? getUserInfoFromToken(token) : null;
  } catch {
    return null;
  }
};
 
const ADMIN_ROLES = new Set(["admin", "super admin"]);

export const isAdminUser = (user) => {
  if (!user) return false;

  const primaryRole = String(user.role || "").toLowerCase();
  if (ADMIN_ROLES.has(primaryRole)) return true;

  const roles = Array.isArray(user.roles) ? user.roles : [];
  return roles.some((role) => ADMIN_ROLES.has(String(role).toLowerCase()));
};

export const getSelectableRoles = (roles, isAdmin = false) => {
  const list = roles || [];
  if (isAdmin) return list;

  // Prefer roles marked visible to non-admins. If none are marked
  // (API/create-role UI never sets displayTrue), show all so assignment works.
  const visible = list.filter((role) => role.displayTrue);
  return visible.length > 0 ? visible : list;
};

export const getDefaultRoles = (roles, isAdmin = false) => {
  const list = roles || [];
  if (isAdmin) {
    return list.filter((role) => role.isDefault);
  }

  const visible = list.filter((role) => role.displayTrue);
  if (visible.length > 0) return visible;

  // Same fallback as selectable: use isDefault when displayTrue is unset.
  return list.filter((role) => role.isDefault);
};

export const getDefaultRoleIds = (roles, isAdmin = false) =>
  getDefaultRoles(roles, isAdmin)
    .map((role) => Number(role.roleId))
    .filter((id) => Number.isFinite(id));

export const getSelectableClientRoles = getSelectableRoles;
export const getDefaultClientRoles = getDefaultRoles;
export const getDefaultClientRoleIds = getDefaultRoleIds;
export const getSelectableProjectRoles = getSelectableRoles;
export const getDefaultProjectRoles = getDefaultRoles;
export const getDefaultProjectRoleIds = getDefaultRoleIds;

const getUserUsername = (user) =>
  user?.uname || user?.username || user?.preferred_username || "";

/**
 * Builds the `users` object for create-client/project APIs.
 * Always includes the logged-in creator by username from the auth token.
 */
export const buildCreateUsersPayload = ({
  userAssignments = [],
  allUsers = [],
  creatorUser,
  roles = [],
  isAdmin = false,
}) => {
  const userById = new Map(
    allUsers.map((user) => [String(user.uid), user]),
  );
  const users = {};

  userAssignments.forEach((entry) => {
    const user = userById.get(String(entry.userId));
    if (!user) return;

    const username = getUserUsername(user);
    if (!username) return;

    const roleIds = (entry.roles || [])
      .map((roleId) => Number(roleId))
      .filter((id) => Number.isFinite(id));

    users[username] = roleIds;
  });

  const creatorUsername = creatorUser?.username;
  if (!creatorUsername) return users;

  const creatorDefaultRoleIds = getDefaultRoleIds(roles, isAdmin);
  const hasCreatorEntry = Object.prototype.hasOwnProperty.call(
    users,
    creatorUsername,
  );

  if (!hasCreatorEntry) {
    users[creatorUsername] = creatorDefaultRoleIds;
    return users;
  }

  if (creatorDefaultRoleIds.length && users[creatorUsername].length === 0) {
    users[creatorUsername] = creatorDefaultRoleIds;
  }

  return users;
};

export const buildCreateClientUsersPayload = ({
  clientRoles = [],
  ...params
} = {}) =>
  buildCreateUsersPayload({
    ...params,
    roles: clientRoles,
  });

export const buildCreateProjectUsersPayload = ({
  projectRoles = [],
  ...params
} = {}) =>
  buildCreateUsersPayload({
    ...params,
    roles: projectRoles,
  });
